import type { IHandleUpdateCampaignTemplate } from '~/modules/campaign';

import type { ICampaignGroup } from '~/modules/campaign-group/types';

import { useCampaignUpdateMutation } from '~/api/graphql/mutations/campaignUpdate.generated';
import { useCampaignGroupStore } from '~/modules/campaign-group/stores';
import { ICampaignType } from '~/modules/campaign/types';
import { useCGTemplateContext } from '../contexts';
import { useCallback } from 'react';
import { deepCopy } from '~/utils';

export const useCGTemplateMonitorCampaignPair = (): {
  handleUpdateCampaignTemplate: IHandleUpdateCampaignTemplate<ICampaignGroup | undefined>;
} => {
  const { handleSetCampaignGroup, handleGetCampaignGroup } = useCGTemplateContext();

  const campaignUpdateMutation = useCampaignUpdateMutation();
  const upsertCampaignGroup = useCampaignGroupStore((state) => state.upsertCampaignGroup);

  const handleUpdateCampaignTemplate: IHandleUpdateCampaignTemplate<ICampaignGroup | undefined> =
    useCallback(
      async (campaign, dataUpdate, ignoreSave = false) => {
        try {
          // Get the current campaign group
          const updatedCampaignGroup = deepCopy(handleGetCampaignGroup());
          if (!updatedCampaignGroup || !updatedCampaignGroup.campaigns) {
            console.error('Campaign group not found or invalid');
            return undefined;
          }

          // Perform the campaign update mutation
          const { campaignUpdate } = await campaignUpdateMutation.mutateAsync({
            id: campaign.id,
            input: dataUpdate,
          });

          if (!campaignUpdate) {
            console.error('Campaign update failed');
            return undefined;
          }

          const campaigns =
            updatedCampaignGroup.campaigns?.filter(
              (item) => item.type === ICampaignType.TEMPLATE_BUNDLE,
            ) || [];
          const campaignIdx = campaigns.findIndex((item) => item.id === campaign.id);
          if (campaignIdx === undefined || campaignIdx === -1) {
            console.error('Campaign not found in campaign group');
            return undefined;
          }
          campaigns[campaignIdx] = {
            ...campaigns[campaignIdx],
            ...campaignUpdate,
          };
          updatedCampaignGroup.campaigns = campaigns;

          if (ignoreSave) return updatedCampaignGroup;

          // const isSyncLivePage = campaignAllowedSyncWithLivePages(updatedCampaignGroup);
          // if (isSyncLivePage) await handleSyncToLivePageCGTemplate();

          handleSetCampaignGroup(updatedCampaignGroup);
          upsertCampaignGroup(updatedCampaignGroup);
          return updatedCampaignGroup;
        } catch (error) {
          console.error('Error updating campaign:', error);
          return undefined;
        }
      },
      [campaignUpdateMutation, handleGetCampaignGroup, handleSetCampaignGroup, upsertCampaignGroup],
    );

  return {
    handleUpdateCampaignTemplate,
  };
};
